import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ProgressLoaderProps {
  petName: string;
  onComplete: () => void;
}
const CIRCLE_RADIUS = 54;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
// Keyframes: [time_ms, target_percent]
// Non-linear progression that feels like real processing
const PROGRESS_KEYFRAMES = [{
  time: 0,
  value: 0
}, {
  time: 300,
  value: 12
}, {
  time: 600,
  value: 25
}, {
  time: 900,
  value: 38
}, {
  time: 1100,
  value: 52
}, {
  time: 1300,
  value: 60
}, {
  time: 1500,
  value: 68
}, {
  time: 1650,
  value: 75
}, {
  time: 1800,
  value: 85
}, {
  time: 1950,
  value: 92
}, {
  time: 2100,
  value: 97
}, {
  time: 2300,
  value: 100
}];
export function ProgressLoader({
  petName,
  onComplete
}: ProgressLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const animFrameRef = useRef<number>();
  const startTimeRef = useRef<number>(0);
  useEffect(() => {
    startTimeRef.current = performance.now();
    function animate(now: number) {
      const elapsed = now - startTimeRef.current;
      // Find the current segment
      let currentValue = 0;
      for (let i = 0; i < PROGRESS_KEYFRAMES.length - 1; i++) {
        const current = PROGRESS_KEYFRAMES[i];
        const next = PROGRESS_KEYFRAMES[i + 1];
        if (elapsed >= current.time && elapsed <= next.time) {
          const segmentProgress = (elapsed - current.time) / (next.time - current.time);
          // Ease-out cubic for smooth deceleration within each segment
          const eased = 1 - Math.pow(1 - segmentProgress, 3);
          currentValue = current.value + (next.value - current.value) * eased;
          break;
        } else if (elapsed > next.time) {
          currentValue = next.value;
        }
      }
      currentValue = Math.min(100, Math.round(currentValue));
      setProgress(currentValue);
      if (currentValue >= 100) {
        setIsDone(true);
        setTimeout(() => {
          onComplete();
        }, 800);
        return;
      }
      animFrameRef.current = requestAnimationFrame(animate);
    }
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [onComplete]);
  const strokeDashoffset = CIRCLE_CIRCUMFERENCE - progress / 100 * CIRCLE_CIRCUMFERENCE;
  const statusMessages = [{
    threshold: 0,
    text: `Analizando el perfil de ${petName || 'tu perro'}...`
  }, {
    threshold: 30,
    text: 'Evaluando comportamientos detectados...'
  }, {
    threshold: 60,
    text: 'Generando rutina personalizada...'
  }, {
    threshold: 85,
    text: 'Preparando tu rutina personalizada…'
  }, {
    threshold: 100,
    text: '¡Tu rutina está lista!'
  }];
  const currentMessage = [...statusMessages].reverse().find(m => progress >= m.threshold)?.text || statusMessages[0].text;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="flex flex-col flex-1 items-center justify-center py-12" data-id="element-313">
      {/* SVG Progress Circle */}
      <div className="relative w-40 h-40 mb-8" data-id="element-314">
        <svg width="160" height="160" viewBox="0 0 120 120" className="transform -rotate-90" data-id="element-315">
          {/* Background circle */}
          <circle cx="60" cy="60" r={CIRCLE_RADIUS} fill="none" stroke="#f5f5f0" strokeWidth="10" data-id="element-316" />
          {/* Progress circle */}
          <circle cx="60" cy="60" r={CIRCLE_RADIUS} fill="none" stroke="url(#progressGradient)" strokeWidth="10" strokeLinecap="round" strokeDasharray={CIRCLE_CIRCUMFERENCE} strokeDashoffset={strokeDashoffset} style={{
          transition: 'stroke-dashoffset 0.15s ease-out'
        }} data-id="element-317" />
          <defs data-id="element-318">
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-319">
              <stop offset="0%" stopColor="#f97316" data-id="element-320" />
              <stop offset="100%" stopColor="#ea580c" data-id="element-321" />
            </linearGradient>
          </defs>
        </svg>

        {/* Center number */}
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-322">
          <motion.span key={progress} className="text-[42px] font-extrabold tracking-tight" style={{
          color: isDone ? '#ea580c' : '#292524'
        }} data-id="element-323">
            {progress}
            <span className="text-[22px] font-bold text-stone-400" data-id="element-324">%</span>
          </motion.span>
        </div>

        {/* Glow effect when complete */}
        <AnimatePresence data-id="element-325">
          {isDone && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="absolute inset-0 rounded-full" style={{
          boxShadow: '0 0 40px rgba(249, 115, 22, 0.25)'
        }} data-id="element-326" />}
        </AnimatePresence>
      </div>

      {/* Status text */}
      <AnimatePresence mode="wait" data-id="element-327">
        <motion.p key={currentMessage} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className={`text-[15px] font-semibold text-center px-6 leading-relaxed ${isDone ? 'text-orange-600' : 'text-stone-500'}`} data-id="element-328">
          {currentMessage}
        </motion.p>
      </AnimatePresence>

      {/* Completion checkmark */}
      <AnimatePresence data-id="element-329">
        {isDone && <motion.div initial={{
        opacity: 0,
        scale: 0,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.4,
        type: 'spring',
        stiffness: 200
      }} className="mt-4 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-lg" data-id="element-330">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-331">
              <polyline points="20 6 9 17 4 12" data-id="element-332" />
            </svg>
          </motion.div>}
      </AnimatePresence>

      {/* Processing dots animation */}
      {!isDone && <div className="flex gap-1.5 mt-6" data-id="element-333">
          {[0, 1, 2].map(i => <motion.div key={i} animate={{
        opacity: [0.3, 1, 0.3],
        scale: [0.8, 1, 0.8]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        delay: i * 0.2,
        ease: 'easeInOut'
      }} className="w-2 h-2 rounded-full bg-orange-400" data-id="element-334" />)}
        </div>}
    </motion.div>;
}